import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, switchMap, take } from 'rxjs';
import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en'
import { ModalController, NavController } from '@ionic/angular';
import { Globals } from './app-globals';
import { ROLE_ADMIN, ADMIN_TEXT, Device, ROLE_MEMBER, MEMBER_TEXT, RoleType, ROLE_VIEWER, VIEWER_TEXT } from './models';
import { FireService } from './fire.service';
import { environment } from 'src/environments/environment';
import { AddGroupPage } from '../groups/add-group/add-group.page';


@Injectable({
  providedIn: 'root'
})
export class BusinessLogicService {

  private tabSelectedSubject = new BehaviorSubject<string>("");
  public tabSelected$ = this.tabSelectedSubject.asObservable();

  private tabSelected!: string;
  private tabSelectedPrevious!: string;

  private timeAgo: TimeAgo;

  // this shows or not the search bar
  private showSearchSubject = new BehaviorSubject<boolean>(false);
  public showSearch$ = this.showSearchSubject.asObservable();

  private showMainMenuSubject = new BehaviorSubject<boolean>(false);
  public showMainMenu$ = this.showMainMenuSubject.asObservable();

  constructor(
    public navCtrl: NavController,
    public fireService: FireService,
    private modalCtrl: ModalController,
  ) {
    TimeAgo.addDefaultLocale(en)
    this.timeAgo = new TimeAgo('en-US');

    this.fireService.loading$
      .pipe(
        filter(loading => !loading),
        switchMap(() => this.fireService.userInfo$),
        take(1))
      .subscribe((userInfo) => {

        if (userInfo.admin) {
          console.log('User is admin');
          this.tabSelected = Globals.TABS.FLEET;
          this.tabSelectedPrevious = Globals.TABS.FLEET;
        } else {
          console.log('User is not admin');
          if (environment.prj === 'tidal') {
            // Set to CHARTS if not admin and condition matches
            this.tabSelected = Globals.TABS.CHARTS; // TODOGG: this needs to happen only when devices > 0
            this.tabSelectedPrevious = Globals.TABS.CHARTS;
          } else if (environment.prj === 'ati') {
            this.tabSelected = Globals.TABS.MAP; // TODOGG: this needs to happen only when devices > 0
            this.tabSelectedPrevious = Globals.TABS.MAP;
          } else {
            this.tabSelected = Globals.TABS.FLEET;
            this.tabSelectedPrevious = Globals.TABS.FLEET;
          }
        }

        this.tabSelectedSubject.next(this.tabSelected);

      });

  }

  setTabSelected(tab: string) {

    console.log('setTabSelected:', tab);

    if (!tab || this.tabSelected === tab) {
      return; // Avoid unnecessary updates
    }

    this.tabSelectedPrevious = this.tabSelected;
    this.tabSelected = tab;

    // Only navigate if changing sections
    if (!this.tabSelected.startsWith(this.tabSelectedPrevious)) {
      this.navCtrl.navigateRoot(`/app/main`);
    }

    this.tabSelectedSubject.next(this.tabSelected);
  }


  setPreviousTabSelected() {

    console.log('setPreviousTabSelected:', this.tabSelectedPrevious);

    if (!this.tabSelectedPrevious || this.tabSelected === this.tabSelectedPrevious) {
      return; // Avoid unnecessary updates
    }

    this.tabSelected = this.tabSelectedPrevious;

    // Only navigate if changing sections
    if (!this.tabSelected.startsWith(this.tabSelectedPrevious)) {
      this.navCtrl.navigateRoot(`/app/main`);
    }

    this.tabSelectedSubject.next(this.tabSelected);
  }


  // this toggles to show or not the search bar
  toggleShowSearch() {
    this.showSearchSubject.next(!this.showSearchSubject.value);
  }

  toggleShowMainMenu() {
    this.showMainMenuSubject.next(!this.showMainMenuSubject.value);
  }

  parseLastSeenTimestamp(timestamp: any): string {
    return this.timeAgo.format(new Date(timestamp.seconds * 1000));
  }

  // calculate if the data is too old
  // too old when it's older than 10 hours
  checkIfDataTooOld(device: Device): boolean {
    if (device.data?.t) {
      let now = new Date();
      let lastSeen = new Date(device.data.t.seconds * 1000);
      let diff = now.getTime() - lastSeen.getTime();
      let diffHours = diff / (1000 * 3600);

      if (diffHours >= 10) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }



  getStatusClass(device: Device) {
    try {
      if (device._dataTooOld) {
        return "style-alert-global";
      } else {
        return "";
      }
    } catch (error) {
      return "style-alert-global";
    }
  }


  getStatus(device: Device) {
    try {

      // if dataTooOld is true, return "offline"
      if (device._dataTooOld) {
        return "Offline";
      }

      // if (device.activeAlerts.length === 0) {
      return "Normal";
      // } else {
      //   return this.getAlerts(device);
      // }
    } catch (error) {
      return "Unknown";
    }
  }



  // check all active alerts and return a string with the alerts
  getAlerts(device: Device) {
    let alertString = "";
    return alertString;
  }





  // tidal specific
  calculateTideHeight(installHeight: number, tide: number): number {
    return installHeight - tide;
  }

  calculateWindOffset(windOffset: number, wind: number): number {
    return (wind + windOffset + 360) % 360;
  }

  calculateSalinity(device: Device | null, ppt: number): number {
    if (!device) {
      return ppt;
    }

    if (!device.calibrationEnabled) {
      return ppt;
    }


    const rawLow = device.rawLow;
    const rawHigh = device.rawHigh;
    const referenceLow = device.referenceLow;
    const referenceHigh = device.referenceHigh;

    const rawRange = rawHigh - rawLow;
    const refRange = referenceHigh - referenceLow;

    // console.log(`Calculating corrected value: rawLow=${rawLow}, rawHigh=${rawHigh}, referenceLow=${referenceLow}, referenceHigh=${referenceHigh}, ppt=${ppt}`);


    // const correctedValue = (ppt - rawLow) * (refRange / rawRange) + referenceLow;
    const correctedValue = Math.round(((ppt - rawLow) * (refRange / rawRange) + referenceLow) * 100) / 100;

    return correctedValue;
  }



  getRoleText(role: RoleType) {
    let text = "";
    switch (role) {
      case ROLE_ADMIN:
        text = ADMIN_TEXT;
        break;
      case ROLE_MEMBER:
        text = MEMBER_TEXT;
        break;
      case ROLE_VIEWER:
        text = VIEWER_TEXT;
        break;
      default:
        text = "-";
        break;
    }
    return text;
  }


}
